import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { kstDateOf } from '@shared/utils/kstTime.util';

import {
  ActiveDeliveryDatePolicy,
  cutoffTimeLabel,
  DeliveryDateChoices,
  isAfterCutoff,
  isSelectableDeliveryDate,
  resolveDeliveryDateChoices,
} from '../utils/giftDeliveryDate.util';
import { scrollToInvalidField } from '../utils/scrollToInvalidField';

/** 마감시각·자정을 넘겼는지 다시 보는 주기 */
const CLOCK_TICK_MS = 20_000;

interface Params {
  /**
   * 주문에 담긴 배송형 상품들의 날짜 정책을 합친 것 (mergeDatePolicies). 없으면 날짜 선택 없음.
   * 목록 refetch로 참조가 바뀌어도 내용이 같으면 선택값을 건드리지 않는다.
   */
  policy: ActiveDeliveryDatePolicy | null;
  /** 배송형 상품 포함 + 일괄배송(Bulk) — 개별 전달은 받는 분이 주소를 넣는 시점이 달라 날짜 지정 대상이 아니다 */
  isBulkDelivery: boolean;
}

export interface GiftCheckoutDeliveryDate {
  /** 날짜 선택 대상일 때의 정책. null이면 섹션을 그리지 않는다 */
  policy: ActiveDeliveryDatePolicy | null;
  /** 지금 시각 기준 선택지 */
  choices: DeliveryDateChoices | null;
  /** 고른 날짜 'YYYY-MM-DD' (미선택 '') */
  selectedDate: string;
  selectDate: (date: string) => void;
  /** 고른 날짜가 마감시각이 지나 풀렸음 — 다시 고르라는 안내용 */
  expired: boolean;
  /** 주문 DTO의 desiredDeliveryDate — 대상이 아니거나 미선택이면 undefined(필드 생략) */
  desiredDeliveryDate: string | undefined;
  /** 결제 진입 검증 — 미선택·마감 경과면 alert + 스크롤 후 false */
  validateDeliveryDate: () => boolean;
  /** 날짜 선택 섹션 ref (검증 실패 시 스크롤 대상) */
  sectionRef: RefObject<HTMLDivElement>;
}

/**
 * 체크아웃(장바구니·바로구매) 공용 — 배송 희망일 선택 상태 + 결제 진입 검증.
 * 주문서를 열어 둔 채 마감시각을 넘기면 선택지가 하루 밀리므로, 주기적으로 시계를 다시 보고
 * 결제 버튼을 누르는 순간에도 한 번 더 확인한다(서버도 같은 규칙으로 400).
 */
export const useGiftCheckoutDeliveryDate = ({
  policy: policyInput,
  isBulkDelivery,
}: Params): GiftCheckoutDeliveryDate => {
  const policyKey = policyInput && isBulkDelivery ? JSON.stringify(policyInput) : '';
  const policy = useMemo<ActiveDeliveryDatePolicy | null>(
    () => (policyKey ? JSON.parse(policyKey) : null),
    [policyKey],
  );

  const [selectedDate, setSelectedDate] = useState('');
  const [expired, setExpired] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // 선택지가 바뀌는 순간(마감시각 통과·자정)에만 값이 달라지는 키 — 같은 값이면 재렌더하지 않는다
  const clockKeyOf = useCallback(
    (now: Date) => (policy ? `${kstDateOf(now)}|${isAfterCutoff(policy, now)}` : ''),
    [policy],
  );
  const [clockKey, setClockKey] = useState(() => clockKeyOf(new Date()));
  useEffect(() => {
    setClockKey(clockKeyOf(new Date()));
    if (!policy) return;
    const timer = setInterval(() => setClockKey(clockKeyOf(new Date())), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [policy, clockKeyOf]);

  const choices = useMemo(
    () => (policy ? resolveDeliveryDateChoices(policy, new Date()) : null),
    // clockKey가 바뀔 때(마감·자정)에만 다시 계산
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [policy, clockKey],
  );

  // 고른 날짜가 더는 선택지에 없으면(마감 경과 등) 풀고 다시 고르게 한다
  useEffect(() => {
    if (!choices || !selectedDate) return;
    if (choices.options.some((o) => o.date === selectedDate && !o.closed)) return;
    setSelectedDate('');
    setExpired(true);
  }, [choices, selectedDate]);

  const selectDate = useCallback((date: string) => {
    setSelectedDate(date);
    setExpired(false);
  }, []);

  const validateDeliveryDate = useCallback(() => {
    if (!policy) return true;
    if (!selectedDate) {
      alert('받으실 날짜를 선택해주세요.');
      scrollToInvalidField(sectionRef.current);
      return false;
    }
    const now = new Date();
    if (!isSelectableDeliveryDate(policy, selectedDate, now)) {
      setClockKey(clockKeyOf(now));
      setSelectedDate('');
      setExpired(true);
      alert(
        `${cutoffTimeLabel(
          policy.cutoffTime,
        )} 결제 마감이 지나 선택하신 날짜에는 받으실 수 없습니다.\n받으실 날짜를 다시 선택해주세요.`,
      );
      scrollToInvalidField(sectionRef.current);
      return false;
    }
    return true;
  }, [policy, selectedDate, clockKeyOf]);

  return {
    policy,
    choices,
    selectedDate: policy ? selectedDate : '',
    selectDate,
    expired: !!policy && expired,
    desiredDeliveryDate: policy && selectedDate ? selectedDate : undefined,
    validateDeliveryDate,
    sectionRef,
  };
};
